import { Children, type PropsWithChildren, type ReactNode } from 'react';
import { isElement } from 'react-is';

import { Intent, IntentProps } from '../../../core/slots/Intent/Intent.js';
import { isSlot } from '../../../core/utils/is-slot.js';
import { ChartToolbar } from '../../core/slots/toolbar/ChartToolbar.js';
import { DownloadData } from '../../core/slots/toolbar/DownloadData.js';
import { ChartToolbarProps } from '../../core/types/toolbar.js';
import { CategoryAxis } from '../slots/CategoryAxis.js';
import { Legend } from '../slots/Legend.js';
import {
  CategoricalBarChartThresholdProps,
  Threshold,
} from '../slots/Threshold.js';
import { Tooltip } from '../slots/Tooltip.js';
import { ValueAxis } from '../slots/ValueAxis.js';
import {
  CategoricalBarChartCategoryAxisProps,
  CategoricalBarChartLegendProps,
  CategoricalBarChartSlots,
  CategoricalBarChartToolbarConfiguration,
  CategoricalBarChartTooltipProps,
  CategoricalBarChartValueAxisProps,
} from '../types/categorical-bar-chart.js';

/**
 * Generate chart config based on slots
 * @param children - CharInteraction children
 * @returns ChartInteraction slot config
 */
const iterateChartToolbarSlots = (
  children: ReactNode,
): CategoricalBarChartToolbarConfiguration => {
  let slots = {};

  Children.forEach(children, (childInteractions) => {
    if (isElement(childInteractions)) {
      switch (childInteractions.type) {
        case DownloadData:
          slots = { ...slots, downloadData: { enabled: true } };
          break;
      }
    }
  });

  return slots;
};

export const iterateSlots = (children: ReactNode) => {
  let slots: CategoricalBarChartSlots = {
    legend: {},
    tooltip: {},
    valueAxis: {},
    categoryAxis: {},
    thresholds: [],
    toolbar: undefined,
  };

  let intents: IntentProps[] | undefined;

  Children.forEach(children, (child) => {
    if (isSlot(child, Legend)) {
      const legend = child.props as CategoricalBarChartLegendProps;

      slots = {
        ...slots,
        legend,
      };
    } else if (isSlot(child, CategoryAxis)) {
      const categoryAxis = child.props as CategoricalBarChartCategoryAxisProps;
      slots = {
        ...slots,
        categoryAxis,
      };
    } else if (isSlot(child, ValueAxis)) {
      const valueAxis = child.props as CategoricalBarChartValueAxisProps;

      slots = {
        ...slots,
        valueAxis,
      };
    } else if (isSlot(child, Tooltip)) {
      const tooltip = child.props as CategoricalBarChartTooltipProps;
      slots = {
        ...slots,
        tooltip,
      };
    } else if (isSlot(child, Threshold)) {
      const thresholdProps = child.props as CategoricalBarChartThresholdProps;

      slots.thresholds.push(thresholdProps);
    } else if (isSlot(child, ChartToolbar)) {
      const { children, ...toolbar } =
        child.props as PropsWithChildren<ChartToolbarProps>;

      const toolbarChildren = iterateChartToolbarSlots(children);

      slots = {
        ...slots,
        toolbar: {
          ...slots.toolbar,
          ...toolbar,
          ...toolbarChildren,
        },
      };
    } else if (isSlot(child, Intent)) {
      intents = intents ? [...intents, child.props] : [child.props];
    }
  });

  if (slots.toolbar) {
    slots = {
      ...slots,
      toolbar: {
        ...slots.toolbar,
        intents,
      },
    };
  }

  return slots;
};
